import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import PersonCell from '../../../components/ui/PersonCell';

const STATUS_COLORS = {
  draft:     { color: '#64748B', bg: '#F1F5F9' },
  submitted: { color: '#0291EF', bg: '#EFF6FF' },
  locked:    { color: '#9333EA', bg: '#FAF5FF' },
  activated: { color: '#16A34A', bg: '#DCFCE7' },
};

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${(p) => p.bg};
  color: ${(p) => p.color};
`;

const useCafeteriaSelectionApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [periods, setPeriods] = useState([]);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    api.get('/v1/open-enrollment-periods?pageSize=200').then((res) => {
      setPeriods(res.data?.data?.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/cafeteria-selections?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams]);

  const fetchById = useCallback(async (id) => {
    try {
      const res = await api.get(`/v1/cafeteria-selections/${id}`);
      return res.data?.data || null;
    } catch {
      return null;
    }
  }, []);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'employeeFullName', label: t('cafeteriaSelection.employee'), options: {
      customBodyRender: (v, tableMeta) => (
        <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.employeeProfilePicture} />
      ), filter: false, sort: true } },
    {
      name: 'openEnrollmentPeriodName', label: t('cafeteriaSelection.period'),
      options: {
        filter: true, sort: false, filterType: 'dropdown',
        filterOptions: { names: periods.map((p) => p.name) },
      },
    },
    {
      name: 'status', label: t('cafeteriaSelection.status'),
      options: {
        filter: true, sort: true, filterType: 'dropdown',
        filterOptions: {
          names: ['draft', 'submitted', 'locked', 'activated'],
          renderValue: (v) => v ? t(`cafeteriaSelection.status${v.charAt(0).toUpperCase()}${v.slice(1)}`, { defaultValue: v }) : '',
        },
        customBodyRender: (v) => {
          const cfg = STATUS_COLORS[v] || STATUS_COLORS.draft;
          return <StatusBadge color={cfg.color} bg={cfg.bg}>
            {t(`cafeteriaSelection.status${v?.charAt(0).toUpperCase()}${v?.slice(1)}`, { defaultValue: v })}
          </StatusBadge>;
        },
      },
    },
    {
      name: 'totalAllocated', label: t('cafeteriaSelection.totalAllocated'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v) => `${Number(v ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺`,
      },
    },
    {
      name: 'budgetAtSubmission', label: t('cafeteriaSelection.budgetAtSubmission'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => `${Number(v ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺`,
      },
    },
    { name: 'submittedAt', label: t('cafeteriaSelection.submittedAt'), options: { filter: false, sort: true, customBodyRender: (v) => v ? new Date(v).toLocaleDateString('tr-TR') : '-' } },
  ], [t, periods, data]);

  return { columns, data, fetching, pagination, fetchById, periods };
};

export default useCafeteriaSelectionApi;
